import React, { FC } from 'react';
import { Stack } from '@mui/material';
import Text from '@ingka/text';
import Button from '@ingka/button';
import useTranslation from '../../hooks/useTranslation';
import ListView, { ListViewItem } from '@ingka/list-view';
import { useDispatch } from 'react-redux';
import {
  enqueueErrorSnackbar,
  enqueueInfoSnackbar,
} from '../../store/notifications/actionWrapper';
import Badge from '@ingka/badge';
import { ProjectTabOverviewProps } from './ProjectTabOverviewProps';
import { getTertial } from '../../utils/utils';
import { getFY } from '../../utils/calculation';
import RoleRestricted from '../RoleRestricted';
import { AD, SA } from '../RoleRestricted/utils';
import {
  addToOnePlan,
  createMSTeams,
} from '../../store/projects/actionWrapper';
import { restoreSlashes } from '../../store/projects/utils';
import { projectFromReltp } from '../../utils/fieldEditingRules';
import { selectUserPermissions } from '../../store/userData/selectors';
import { useTypedSelector } from '../../store';
import { checkIfAllowed } from '../../utils/restrictionFile';
import { checkForConfidential } from '../../containers/App/utils';
import AddMsTeamsLinkPrompt from '../AddMsTeamsLinkPrompt';
import { selectMsTeamsModal } from '../../store/modals/selectors';
import {
  openMsTeamsModal,
  closeMsTeamsModal,
} from '../../store/modals/actionWrappers';
import { getProjectResponsiblesMapByKey } from './utils';
import { useFormContext } from 'react-hook-form';

const ProjectTabOverview: FC<ProjectTabOverviewProps> = ({
  project,
  setActiveProject,
  onViewDirective,
}) => {
  const { watch } = useFormContext();
  const disabledFields = watch('disabledFields');
  const t = useTranslation();
  const dispatch = useDispatch();
  const userPermissions = useTypedSelector(selectUserPermissions);
  const msTeamsModalVisible = useTypedSelector(selectMsTeamsModal);

  const handleUpdateProjectStatus = (projectId: string) => {
    setActiveProject(projectId);
  };

  const addToOnePlanHandler = () => {
    dispatch(addToOnePlan(project?.id));
  };

  const createMSTeamsHandler = () => {
    dispatch(openMsTeamsModal());
  };

  const handleCopy = (event: React.MouseEvent, data: string | undefined) => {
    event.preventDefault();

    if (!data) {
      dispatch(enqueueErrorSnackbar(t('snackbarMessages.noDataToCopy')));
      return;
    }

    navigator.clipboard
      .writeText(data)
      .then(() => {
        dispatch(enqueueInfoSnackbar(t('snackbarMessages.copied')));
      })
      .catch(() => {
        dispatch(enqueueErrorSnackbar(t('snackbarMessages.failedToCopy')));
      });
  };
  const statusButtonDisabled = projectFromReltp(project?.projectSource);

  const handleCloseMsTeamsModal = () => {
    dispatch(closeMsTeamsModal());
  };

  const handleAcceptMsTeamsLink = (link?: string) => {
    const members = getProjectResponsiblesMapByKey(
      project?.projectResponsibleList,
    );

    dispatch(
      createMSTeams({
        projectId: project?.id,
        referenceId: project?.referenceId,
        projectName: project?.projectName,
        isTesting: true,
        ownerEmail: project?.countryProjectResponsible,
        link: link && restoreSlashes(link, '____'),
        members: {
          countryProjectResponsible: project?.countryProjectResponsible,
          ...members,
        },
      }),
    );

    dispatch(closeMsTeamsModal());
    dispatch(enqueueInfoSnackbar('MS Teams link added!'));
  };

  return (
    <Stack pb={5} spacing={3} px={2} flex={1}>
      <Text tagName={'h4'} textStyle="Heading.S">
        {`${project?.projectName} - ${project?.unitType}`}
      </Text>
      <Stack direction={'row'} spacing={2} data-testid={'statuses'}>
        {checkForConfidential(project?.confidential) && (
          <Badge
            label={t(`confidentialityLevels.${project?.confidential}`)}
            size={'small'}
            colour={'black'}
            prefix={'testid-confidential '}
          />
        )}
        {project?.isep && (
          <Badge
            label={'ISEP'}
            size={'small'}
            colour={'grey'}
            prefix={'testid-isep '}
          />
        )}
        {project?.establishmentType && (
          <Badge
            label={project.establishmentType}
            size={'small'}
            colour={'grey'}
            prefix={'testid-establishmentType '}
          />
        )}

        <Badge
          label={t(`statuses.${project?.projectStatus}`)}
          size={'small'}
          colour={'grey'}
          prefix={'testid-status '}
        />
      </Stack>
      {!disabledFields?.includes('buttons') && (
        <Stack direction={'row'} spacing={2}>
          {!window.location.href.includes('approval') && (
            <>
              <Button
                onClick={() => handleUpdateProjectStatus(project.id)}
                size={'small'}
                disabled={statusButtonDisabled}
              >
                {t('labels.updateProjectStatus')}
              </Button>
            </>
          )}
          {onViewDirective && project?.directive && (
            <Button
              size={'small'}
              onClick={onViewDirective}
              data-testid={'view-project-directive-btn'}
            >
              {t('labels.viewProjectDirective')}
            </Button>
          )}

          <RoleRestricted
            roles={[AD, SA]}
            confidentialRestricted={['INTERNAL', 'CONFIDENTIAL']}
            confidentialLevel={project?.confidential}
          >
            <Button size={'small'} onClick={createMSTeamsHandler}>
              {'Connect to MS Teams'}
            </Button>
          </RoleRestricted>
          <RoleRestricted roles={[AD, SA]}>
            <Button
              size={'small'}
              onClick={addToOnePlanHandler}
              disabled={
                project?.partOfOnePlan ||
                !checkIfAllowed(userPermissions, 'onePlanAdmin')
              }
            >
              {project?.partOfOnePlan
                ? 'Connected to One Plan'
                : 'Connect to One Plan'}
            </Button>
          </RoleRestricted>
        </Stack>
      )}

      <Stack direction={'row'} spacing={2}>
        <ListView
          style={{ flex: 1 }}
          id="list-items-overview-tab-1"
          showDivider
          size="medium"
        >
          <ListViewItem
            emphasised
            data-testid={'project-id'}
            href="#"
            leadingIconPosition="center"
            addOn={t('projects.properties.projectID')}
            title={project?.referenceId}
            control={'navigational'}
            controlIcon={'copy'}
            onClick={(event) => handleCopy(event, project?.referenceId)}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('assets.properties.unitId')}
            title={project?.etrUnitId}
            control={'navigational'}
            controlIcon={'copy'}
            onClick={(event) => handleCopy(event, project?.etrUnitId)}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('labels.openingFyTertial')}
            title={`${getFY(0, project?.targetProjectEndDate)} - T${getTertial(project?.targetProjectEndDate)}`}
            control={'default'}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('ppm.isep.description')}
            title={project?.isep ? t('common.yes') : t('common.no')}
            control={'default'}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn="Invex"
            title="Decision points"
            control={'navigational'}
            controlIcon={'linkOut'}
            onClick={(e) => {
              e.preventDefault();
              window.open(
                project?.decisionPoints
                  ? `https://invex.ingka.com/requests/${project?.decisionPoints[0].requestId}`
                  : 'https://invex.ingka.com/',
                '_blank',
              );
            }}
          />
        </ListView>
        {/*TODO PPM- implement DP tab*/}
        {/*<ListViewItem*/}
        {/*  emphasised={true}*/}
        {/*  href="#"*/}
        {/*  leadingIconPosition="center"*/}
        {/*  addOn={t('labels.DP2')}*/}
        {/*  title={t('common.activeDecisionPoint')}*/}
        {/*  control={'navigational'}*/}
        {/*  controlIcon={'arrowRight'}*/}
        {/*  onClick={handleRedirectToDP}*/}
        {/*/>*/}
        <ListView
          style={{ flex: 1 }}
          id="list-items-overview-tab-2"
          showDivider
          size="medium"
        >
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('labels.CBDName')}
            title={project?.businessUnitType}
            control={'navigational'}
            controlIcon={'copy'}
            onClick={(event) => handleCopy(event, project?.businessUnitType)}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('labels.businessUnitId')}
            title={project?.businessUnitId}
            control={'navigational'}
            controlIcon={'copy'}
            onClick={(event) => handleCopy(event, project?.businessUnitId)}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('projects.properties.mtoolId')}
            title={project?.mtoolId}
            control={'navigational'}
            controlIcon={'copy'}
            onClick={(event) => handleCopy(event, project?.mtoolId)}
          />
          <ListViewItem
            emphasised
            href="#"
            leadingIconPosition="center"
            addOn={t('projects.properties.unifierID')}
            title={project?.unifierId}
            control={'navigational'}
            controlIcon={'copy'}
            onClick={(event) => handleCopy(event, project?.unifierId)}
          />
          {project?.msTeamsUrl && (
            <ListViewItem
              emphasised
              href="#"
              leadingIconPosition="center"
              addOn={t('projects.properties.msTeams')}
              title={
                restoreSlashes(project?.msTeamsUrl, '____').slice(0, 35) + '...'
              }
              control={'navigational'}
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  restoreSlashes(project?.msTeamsUrl, '____'),
                  '_blank',
                );
              }}
              style={{ width: 100 }}
            />
          )}
        </ListView>
      </Stack>
      <AddMsTeamsLinkPrompt
        isVisible={msTeamsModalVisible}
        passedLink={
          project?.msTeamsUrl ? restoreSlashes(project?.msTeamsUrl, '____') : ''
        }
        onClose={handleCloseMsTeamsModal}
        onCancel={handleCloseMsTeamsModal}
        onAccept={handleAcceptMsTeamsLink}
      />
    </Stack>
  );
};

export default ProjectTabOverview;
